import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Heart, Share2, Edit3, Trash2, Shirt, Calendar, Tag, Star, Eye, ImageIcon, Package } from 'lucide-react';
import OutfitPreview from './OutfitPreview';
import ModelViewer from './ModelViewer';
import { CLOSET } from '../data/closet';
import { normalizePiece } from '../utils/pieceNormalizer';
import { getCapsuleForPiece } from '../utils/supabaseDatabase';
import VerifiedBadge from './VerifiedBadge';

const OutfitDetailModal = ({ 
  isOpen, 
  outfit, 
  onClose, 
  onToggleFavorite, 
  onEdit, 
  onDelete, 
  onShare,
  solPrice = 100 
}) => {
  const navigate = useNavigate();
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, piece: null });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [modalViewMode, setModalViewMode] = useState('thumbnail'); // 'thumbnail' or '3d'
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null); // Track capsule info for current piece

  // Check if piece belongs to a capsule when modal opens
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.piece?.id) {
      setPieceCapsuleInfo(null);
      return;
    }

    const checkCapsule = async () => {
      try {
        let originalId = pieceDetailModal.piece.id;
        const itemIdString = String(pieceDetailModal.piece.id);
        
        if (itemIdString.startsWith('supabase_')) {
          originalId = itemIdString.replace('supabase_', '');
          if (!isNaN(originalId)) {
            originalId = parseInt(originalId, 10);
          }
        } else if (itemIdString.startsWith('local_')) {
          console.log('🔍 Local piece detected, skipping capsule check');
          setPieceCapsuleInfo(null);
          return;
        }
        
        console.log('🔍 Checking capsule for piece:', { originalId, transformedId: pieceDetailModal.piece.id });
        const capsuleInfo = await getCapsuleForPiece(originalId);
        console.log('🔍 Capsule info result:', capsuleInfo);
        
        setPieceCapsuleInfo(capsuleInfo);
      } catch (error) {
        console.error('❌ Error fetching capsule info:', error);
        setPieceCapsuleInfo(null);
      }
    };

    checkCapsule();
  }, [pieceDetailModal.isOpen, pieceDetailModal.piece]);

  // Early return after all hooks are declared
  if (!isOpen || !outfit) return null;

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet || item?.creatorId;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Debug: Log outfit structure
  console.log('OutfitDetailModal - outfit data:', outfit);
  console.log('OutfitDetailModal - outfit.pieces (count):', outfit.pieces);
  console.log('OutfitDetailModal - outfit.models (array):', outfit.models);
  console.log('OutfitDetailModal - outfit.models type:', typeof outfit.models);
  console.log('OutfitDetailModal - Array.isArray(outfit.models):', Array.isArray(outfit.models));
  console.log('OutfitDetailModal - Object.keys(outfit):', Object.keys(outfit));
  
  // Debug: Log individual model data
  if (Array.isArray(outfit.models)) {
    outfit.models.forEach((model, index) => {
      console.log(`Model ${index}:`, {
        name: model.name,
        price: model.price,
        category: model.category,
        modelUrl: model.modelUrl
      });
    });
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  // Helper function to get price from closet data if missing from model
  const getModelPrice = (model) => {
    // First try to use the price from the model itself
    if (model.price && parseFloat(model.price) > 0) {
      return parseFloat(model.price);
    }
    
    // If no price in model, try to find it in the closet data by ID
    if (model.id) {
      const closetItem = CLOSET.find(item => item.id === model.id);
      if (closetItem && closetItem.price) {
        console.log(`Found price in closet for ${model.name}: ${closetItem.price} SOL`);
        return parseFloat(closetItem.price);
      }
    }
    
    // If still no price found, try to match by name and category
    const closetItem = CLOSET.find(item => 
      item.name === model.name && item.category === model.category
    );
    if (closetItem && closetItem.price) {
      console.log(`Found price in closet by name/category for ${model.name}: ${closetItem.price} SOL`);
      return parseFloat(closetItem.price);
    }
    
    console.log(`No price found for ${model.name}, using 0`);
    return 0;
  };

  // Calculate total price - use models array and fetch prices from closet if needed
  const totalPrice = Array.isArray(outfit.models) 
    ? outfit.models.reduce((sum, model) => {
        const price = getModelPrice(model);
        console.log(`Model ${model.name} final price: ${price} SOL`);
        return sum + price;
      }, 0) 
    : 0;
  
  console.log('Total outfit price calculated:', totalPrice);

  // Handle piece detail modal
  const openPieceDetailModal = (model) => {
    // Transform the model data to match the expected piece structure
    const pieceData = {
      id: model.id,
      name: model.name,
      description: model.description,
      category: model.category || 'Uncategorized',
      color: model.color,
      price: model.price,
      tags: model.tags || [],
      creator: model.creator || 'Unknown',
      creatorId: model.creatorId,
      originalCreator: model.originalCreator, // Include originalCreator object
      creators: model.originalCreator, // Also as creators for compatibility
      holder: model.holder || 'You',
      holderId: model.holderId,
      holderAddress: model.holderAddress || 'Current Owner',
      createdAt: model.createdAt,
      modelUrl: model.modelUrl || model.dataUrl, // Use dataUrl as fallback
      thumbnail: model.thumbnail,
      thumbnailUrl: model.thumbnailUrl,
      image: model.image,
      quantity: 1,
    };
    
    console.log('Opening piece detail modal with RAW data:', pieceData);
    
    // Normalize the piece to ensure consistent field structure
    const normalizedPiece = normalizePiece(pieceData);
    console.log('Opening piece detail modal with NORMALIZED data:', normalizedPiece);
    
    setPieceDetailModal({ isOpen: true, piece: normalizedPiece });
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, piece: null });
    setModalViewMode('thumbnail'); // Reset to thumbnail view
    setPieceCapsuleInfo(null);
  };

  // Handle view entire capsule button click
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo?.id) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
      closePieceDetailModal();
      onClose(); // Also close the outfit modal
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = () => {
    setDeleteConfirmModal(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(outfit.id);
    onClose();
    setDeleteConfirmModal(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal(false);
  };

  return (
    <>
      {/* Piece Detail Modal - Exact Explore Style */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
              {/* Left side - Thumbnail/3D Model & Creator Info */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {pieceDetailModal.piece?.modelUrl && (
                  <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                    <button
                      onClick={() => setModalViewMode('3d')}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                        modalViewMode === '3d'
                          ? 'text-white bg-[#2a2a2a] shadow-lg' 
                          : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                      }`}
                    >
                      3D Model
                    </button>
                    <button
                      onClick={() => setModalViewMode('thumbnail')}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                        modalViewMode === 'thumbnail'
                          ? 'text-white bg-[#2a2a2a] shadow-lg' 
                          : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                      }`}
                    >
                      Thumbnail
                    </button>
                  </div>
                )}
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
                  {modalViewMode === '3d' && pieceDetailModal.piece?.modelUrl ? (
                    // Show 3D model
                    <ModelViewer
                      src={pieceDetailModal.piece?.modelUrl}
                      posterUrl={pieceDetailModal.piece?.thumbnailUrl || pieceDetailModal.piece?.thumbnail?.dataUrl || '/images/piece-placeholder.png'}
                      className="w-full h-full modal"
                    />
                  ) : (
                    // Show thumbnail image
                    pieceDetailModal.piece?.thumbnailUrl || pieceDetailModal.piece?.thumbnail?.dataUrl || pieceDetailModal.piece?.image ? (
                      <img 
                        src={pieceDetailModal.piece?.thumbnailUrl || pieceDetailModal.piece?.thumbnail?.dataUrl || pieceDetailModal.piece?.image} 
                        alt={pieceDetailModal.piece?.name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    ) : (
                      <img 
                        src="/images/piece-placeholder.png" 
                        alt="No preview available"
                        className="w-full h-full object-cover"
                      />
                    )
                  )}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.piece)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(pieceDetailModal.piece?.originalCreator?.profilePicture || pieceDetailModal.piece?.creator?.profilePicture || pieceDetailModal.piece?.originalCreator?.profile_picture_url || pieceDetailModal.piece?.creator?.profile_picture_url) ? (
                          <img
                            src={pieceDetailModal.piece?.originalCreator?.profilePicture || pieceDetailModal.piece?.creator?.profilePicture || pieceDetailModal.piece?.originalCreator?.profile_picture_url || pieceDetailModal.piece?.creator?.profile_picture_url}
                            alt={pieceDetailModal.piece?.originalCreator?.username || pieceDetailModal.piece?.creator?.username || pieceDetailModal.piece?.creator || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(pieceDetailModal.piece?.originalCreator?.username || pieceDetailModal.piece?.creator?.username || pieceDetailModal.piece?.creator || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {pieceDetailModal.piece?.originalCreator?.username || pieceDetailModal.piece?.creator?.username || pieceDetailModal.piece?.creator || 'Original Creator'}
                          </div>
                          {(pieceDetailModal.piece?.originalCreator?.verified || pieceDetailModal.piece?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={pieceDetailModal.piece?.originalCreator?.verificationType || pieceDetailModal.piece?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {pieceDetailModal.piece?.originalCreator?.piecesCount || pieceDetailModal.piece?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.piece?.name}
                      </h2>
                    </div>
                    {/* Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.piece?.tags && pieceDetailModal.piece.tags.length > 0 ? (
                        pieceDetailModal.piece.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium capitalize">
                          {pieceDetailModal.piece?.category || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.piece?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{pieceDetailModal.piece.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.piece?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {parseFloat(pieceDetailModal.piece?.price || 0).toFixed(2)} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${((pieceDetailModal.piece?.price || 0) * solPrice).toFixed(2)} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.piece?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {pieceDetailModal.piece?.createdAt ? new Date(pieceDetailModal.piece.createdAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : '10/13/25'}
                  </div>

                  {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                  {pieceCapsuleInfo && (
                    <div className="pt-3">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCapsule();
                        }}
                        className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                      >
                        <Package size={14} />
                        View entire capsule
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
      <div 
        className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-full">
          {/* Left side - Individual Piece Cards */}
          <div className="w-1/2 p-6 border-r border-[#2a2a2a] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="text-white font-semibold">Outfit Pieces</h3>
              {Array.isArray(outfit.models) && outfit.models.length > 0 && (
                <span className="text-gray-400 text-sm bg-[#1a1a1a] px-2 py-1 rounded-full border border-[#2a2a2a]">
                  {outfit.models.length} {outfit.models.length === 1 ? 'piece' : 'pieces'}
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 hover:scrollbar-thumb-gray-500 max-h-[500px]">
            {Array.isArray(outfit.models) && outfit.models.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 pb-4">
                {outfit.models.map((model, index) => (
                  <div 
                    key={index} 
                    className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-3 hover:border-white/50 transition-all duration-200 cursor-pointer"
                    onClick={() => openPieceDetailModal(model)}
                  >
                    {/* Piece Thumbnail */}
                    <div className="aspect-square bg-[#2a2a2a] rounded-lg flex items-center justify-center mb-2 overflow-hidden">
                      {(() => {
                        // Debug: Log the model data to see what we have
                        console.log('Model thumbnail data:', {
                          name: model.name,
                          thumbnail: model.thumbnail,
                          thumbnailUrl: model.thumbnailUrl,
                          image: model.image
                        });
                        
                        // Try multiple thumbnail sources
                        const thumbnailSrc = model.thumbnail?.dataUrl || model.thumbnailUrl || model.image;
                        
                        if (thumbnailSrc) {
                          return (
                            <img 
                              src={thumbnailSrc} 
                              alt={model.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.error('Failed to load thumbnail:', thumbnailSrc);
                                e.target.style.display = 'none';
                              }}
                            />
                          );
                        }
                        
                        return (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                            <ImageIcon size={32} className="mb-2 text-gray-500" />
                            <div className="text-xs text-center px-2">No Preview Available</div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Piece Name */}
                    <div className="text-center">
                      <h4 className="text-white font-medium text-sm truncate">
                        {model.name}
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">👕</div>
                <div className="text-gray-400 text-sm">No pieces in this outfit</div>
              </div>
            )}
            </div>

            {/* Quick Actions */}
            <div className="flex gap-3 mt-6 flex-shrink-0">
              <button 
                onClick={() => onToggleFavorite(outfit.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                  outfit.isFavorite 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                    : 'bg-[#2a2a2a] text-gray-400 hover:bg-[#3a3a3a] hover:text-red-400 border border-[#2a2a2a]'
                }`}
              >
                <Heart size={18} className={outfit.isFavorite ? 'fill-current' : ''} />
                {outfit.isFavorite ? 'Favorited' : 'Favorite'}
              </button>
              <button 
                onClick={() => onShare(outfit)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-400 hover:text-white rounded-xl transition-all duration-200 font-medium border border-[#2a2a2a]"
              >
                <Share2 size={18} />
                Share
              </button>
            </div>
          </div>

          {/* Right side - Outfit Details */}
          <div className="w-1/2 p-6 overflow-y-auto">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                {outfit.name}
              </h2>
              
              {/* Tags */}
              {outfit.tags && outfit.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {outfit.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-[#1a1a1a] text-gray-300 text-sm rounded-full border border-[#2a2a2a]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Description */}
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h3 className="text-white font-semibold mb-2">Description</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {outfit.description || 'A carefully curated outfit combination with premium digital fashion pieces.'}
                </p>
              </div>

              {/* Outfit Stats */}
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h3 className="text-white font-semibold mb-4">Outfit Statistics</h3>
                <div className="flex items-center gap-3">
                  <img 
                    src="/images/piece-placeholder.png" 
                    alt="Piece"
                    className="w-7 h-7 object-contain"
                  />
                  <div>
                    <div className="text-white font-medium">{Array.isArray(outfit.models) ? outfit.models.length : outfit.pieces || 0}</div>
                    <div className="text-gray-400 text-xs">
                      {Array.isArray(outfit.models) 
                        ? (outfit.models.length === 1 ? 'Piece' : 'Pieces')
                        : ((outfit.pieces || 0) === 1 ? 'Piece' : 'Pieces')
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h3 className="text-white font-semibold mb-4">Pricing</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Total Value</span>
                    <div className="text-right">
                      <div className="text-green-400 font-bold text-xl">
                        {totalPrice.toFixed(2)} SOL
                      </div>
                      <div className="text-gray-500 text-sm">
                        ≈ ${(totalPrice * solPrice).toFixed(2)} USD
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => onEdit(outfit.id)}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-2"
                >
                  <Edit3 size={16} />
                  Edit Outfit
                </button>
                <button 
                  onClick={handleDeleteClick}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-red-500/25"
                >
                  <Trash2 size={16} />
                  Delete Outfit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <Trash2 size={24} className="text-red-400" />
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-2">Delete Outfit</h3>
              <p className="text-gray-400 mb-6">
                Are you sure you want to delete "{outfit.name}"? This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-300 hover:text-white rounded-xl transition-all duration-200 font-medium border border-[#2a2a2a]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-red-500/25"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default OutfitDetailModal;
